import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  LinearProgress,
  CircularProgress,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { IgnitionProgress, IgnitionProgressStatus, SimulationProgressData, useSimulationProgress } from "../../api/simulations";

interface Props {
  open: boolean;
  onClose: () => void;
  simulationId: string | null;
  simulationName?: string;
}

const STATUS_CONFIG: Record<
  IgnitionProgressStatus,
  { label: string; color: string; bgColor: string; borderColor: string }
> = {
  QUEUED:     { label: "Queued",     color: "rgba(255,255,255,0.5)", bgColor: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.15)" },
  SIMULATING: { label: "Simulating", color: "#ff9800",               bgColor: "rgba(255,152,0,0.12)",   borderColor: "rgba(255,152,0,0.35)"   },
  COMPLETE:   { label: "Complete",   color: "#4caf50",               bgColor: "rgba(76,175,80,0.12)",   borderColor: "rgba(76,175,80,0.35)"   },
  ERROR:      { label: "Error",      color: "#f44336",               bgColor: "rgba(244,67,54,0.12)",   borderColor: "rgba(244,67,54,0.35)"   },
};

function formatHoursMinutes(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}hr`;
  return `${h}hr ${m}min`;
}

function formatFireSize(burntPixels: number | null, resolutionM: number | null): string | null {
  if (burntPixels == null || resolutionM == null || burntPixels <= 0) return null;
  const ha = (burntPixels * resolutionM * resolutionM) / 10000;
  if (ha >= 1_000_000) return `${(ha / 1_000_000).toFixed(1)}M ha`;
  if (ha >= 1000) return `${(ha / 1000).toFixed(1)}k ha`;
  return `${ha.toFixed(0)} ha`;
}

function ignitionProgressPercent(ig: IgnitionProgress): number {
  if (ig.status === "COMPLETE") return 100;
  if (ig.status === "ERROR") return 0;
  const simulated = ig.hours_simulated ?? 0;
  const total = ig.total_hours ?? 0;
  if (total <= 0) return 0;
  return Math.min(100, (simulated / total) * 100);
}

const IgnitionRow: React.FC<{ ig: IgnitionProgress; index: number }> = ({ ig, index }) => {
  const cfg = STATUS_CONFIG[ig.status] ?? STATUS_CONFIG.QUEUED;
  const pct = ignitionProgressPercent(ig);
  const showTime = ig.status === "SIMULATING" || ig.status === "COMPLETE";
  const fireSize = formatFireSize(ig.burnt_pixels, ig.resolution_m);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.07)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WhatshotIcon sx={{ fontSize: 14, color: "rgba(255,107,53,0.8)" }} />
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 500 }}>
            Ignition {index + 1}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {fireSize && (
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)" }}>
              ~{fireSize}
            </Typography>
          )}
          <Chip
            label={cfg.label}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.68rem",
              fontWeight: 600,
              color: cfg.color,
              bgcolor: cfg.bgColor,
              border: `1px solid ${cfg.borderColor}`,
              "& .MuiChip-label": { px: 1 },
            }}
          />
        </Box>
      </Box>

      <LinearProgress
        variant={ig.status === "SIMULATING" && pct === 0 ? "indeterminate" : "determinate"}
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: "rgba(255,255,255,0.07)",
          "& .MuiLinearProgress-bar": {
            borderRadius: 3,
            bgcolor:
              ig.status === "COMPLETE"
                ? "#4caf50"
                : ig.status === "ERROR"
                ? "#f44336"
                : "#ff6b35",
          },
        }}
      />

      {showTime && ig.total_hours != null && (
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>
          {formatHoursMinutes(ig.hours_simulated ?? 0)} of {formatHoursMinutes(ig.total_hours)} simulated
        </Typography>
      )}
    </Box>
  );
};

// The backend's preparation stages, in execution order. The current stage
// comes from the progress endpoint's prep_stage; earlier stages are shown
// done, later ones pending. "starting_workers" is synthetic: prep_stage is
// "done" but no worker has started an ignition yet (ECS scaling up).
const PREP_STAGES: Array<{ key: string; label: string }> = [
  { key: "queued", label: "Waiting for a simulation worker" },
  { key: "loading_layers", label: "Loading project layers" },
  { key: "preparing_rasters", label: "Preparing terrain and fuel rasters" },
  { key: "uploading_manifest", label: "Uploading simulation inputs" },
  { key: "enqueueing_tasks", label: "Enqueueing simulation tasks" },
  { key: "starting_workers", label: "Starting simulation workers" },
];

// Preparation heartbeats at least once per fuel tile / SQS batch; a quiet
// spell this long means the prep task has likely stalled or died.
const PREP_STALE_AFTER_MS = 3 * 60 * 1000;

// After enqueue, ECS may scale workers up from zero; that routinely takes a
// few minutes, so only warn beyond this.
const WORKER_START_SLOW_AFTER_MS = 10 * 60 * 1000;

function formatDurationShort(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

// Re-render every second so elapsed/heartbeat readouts stay live between polls.
const useNowTick = (active: boolean): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
};

// Stage-by-stage view of the preparation phase (status PREPARING): which
// stages are done, what the prep task is doing right now, how long the run
// has been preparing, and a warning when the heartbeat goes quiet.
const PrepTimeline: React.FC<{ progress: SimulationProgressData }> = ({ progress }) => {
  const now = useNowTick(true);

  // prep_stage === "done" means the prep task finished; the run is now in the
  // synthetic "starting workers" stage until an ignition begins simulating.
  const startingWorkers = progress.prep_stage === "done";
  const currentIdx = startingWorkers
    ? PREP_STAGES.length - 1
    : PREP_STAGES.findIndex((s) => s.key === progress.prep_stage);
  const createdMs = progress.created_at ? Date.parse(progress.created_at) : NaN;
  const heartbeatMs = progress.prep_heartbeat_at ? Date.parse(progress.prep_heartbeat_at) : NaN;
  const elapsed = Number.isFinite(createdMs) ? formatDurationShort(now - createdMs) : null;
  const sinceHeartbeat = Number.isFinite(heartbeatMs) ? Math.max(0, now - heartbeatMs) : null;
  // During prep, heartbeat silence means the prep task may have died. During
  // worker start-up there is no heartbeat by design — the same clock instead
  // measures time since enqueue, with a more generous threshold.
  const isStale =
    sinceHeartbeat !== null &&
    sinceHeartbeat > (startingWorkers ? WORKER_START_SLOW_AFTER_MS : PREP_STALE_AFTER_MS);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 500 }}>
          Preparing simulation
        </Typography>
        {elapsed && (
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)" }}>
            {elapsed} elapsed
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {PREP_STAGES.map((stage, i) => {
          // Unknown/legacy stage keys (currentIdx -1): show everything pending.
          const isDone = currentIdx >= 0 && i < currentIdx;
          const isCurrent = currentIdx === i;
          return (
            <Box
              key={stage.key}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.25,
                p: 1,
                borderRadius: 1.5,
                bgcolor: isCurrent ? "rgba(255,152,0,0.08)" : "transparent",
                border: isCurrent ? "1px solid rgba(255,152,0,0.25)" : "1px solid transparent",
              }}
            >
              <Box sx={{ pt: "1px", display: "flex" }}>
                {isDone ? (
                  <CheckCircleIcon sx={{ fontSize: 18, color: "#4caf50" }} />
                ) : isCurrent ? (
                  <CircularProgress size={16} thickness={5} sx={{ color: "#ff9800" }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: "rgba(255,255,255,0.25)" }} />
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: isCurrent
                      ? "rgba(255,255,255,0.9)"
                      : isDone
                      ? "rgba(255,255,255,0.6)"
                      : "rgba(255,255,255,0.35)",
                    fontWeight: isCurrent ? 600 : 400,
                  }}
                >
                  {stage.label}
                </Typography>
                {isCurrent && progress.prep_detail && (
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block" }}>
                    {progress.prep_detail}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1.5 }}>
        {isStale ? (
          <>
            <WarningAmberIcon sx={{ fontSize: 16, color: "#ffb74d" }} />
            <Typography variant="caption" sx={{ color: "#ffb74d" }}>
              {startingWorkers
                ? `Workers are taking unusually long to start (${formatDurationShort(sinceHeartbeat!)} since tasks were enqueued).`
                : `No updates for ${formatDurationShort(sinceHeartbeat!)}.`}
            </Typography>
          </>
        ) : startingWorkers ? (
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.35)" }}>
            {sinceHeartbeat !== null
              ? `Tasks enqueued ${formatDurationShort(sinceHeartbeat)} ago — `
              : ""}
            compute is scaling up; the first ignitions usually start within a few minutes.
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.35)" }}>
            {sinceHeartbeat !== null
              ? `Last update ${formatDurationShort(sinceHeartbeat)} ago`
              : "Waiting for the first progress report…"}
            {" · Preparation can take a while on large projects."}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const SimulationProgressModal: React.FC<Props> = ({ open, onClose, simulationId, simulationName }) => {
  const { data: progressData } = useSimulationProgress(open ? simulationId : null);
  const ignitions: IgnitionProgress[] = progressData?.ignitions ?? [];

  // Keep the prep timeline up (on its final "Starting simulation workers"
  // stage) after enqueue until a worker actually begins an ignition —
  // otherwise this modal shows a wall of 0% "Queued" rows with no explanation
  // while ECS scales up.
  const showPrepTimeline =
    !!progressData &&
    (progressData.status === "PREPARING" ||
      (progressData.status === "PENDING" &&
        progressData.prep_stage === "done" &&
        ignitions.length > 0 &&
        ignitions.every((ig) => ig.status === "QUEUED")));

  const totalCount = ignitions.length;
  const completeCount = ignitions.filter((ig: IgnitionProgress) => ig.status === "COMPLETE").length;
  const errorCount = ignitions.filter((ig: IgnitionProgress) => ig.status === "ERROR").length;

  const overallPct =
    totalCount > 0
      ? ignitions.reduce((sum: number, ig: IgnitionProgress) => sum + ignitionProgressPercent(ig), 0) / totalCount
      : 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#1a1f2e",
          color: "#fff",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
            Simulation Progress
          </Typography>
          {simulationName && (
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)" }}>
              {simulationName}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "rgba(255,255,255,0.5)" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 2 }}>
        {showPrepTimeline ? (
          <PrepTimeline progress={progressData!} />
        ) : totalCount === 0 ? (
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.4)", py: 2, textAlign: "center" }}>
            Waiting for ignition data…
          </Typography>
        ) : (
          <>
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 500 }}>
                  Overall progress
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>
                  {completeCount} / {totalCount} ignitions
                  {errorCount > 0 && (
                    <Box component="span" sx={{ color: "#f44336", ml: 0.5 }}>
                      ({errorCount} error{errorCount > 1 ? "s" : ""})
                    </Box>
                  )}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={overallPct}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: "rgba(255,255,255,0.08)",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 5,
                    bgcolor: "#ff6b35",
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.35)", display: "block", mt: 0.5 }}
              >
                {overallPct.toFixed(0)}% average across all ignitions
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, maxHeight: 420, overflowY: "auto" }}>
              {ignitions.map((ig: IgnitionProgress, i: number) => (
                <IgnitionRow key={ig.ignition_idx} ig={ig} index={i} />
              ))}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SimulationProgressModal;
